import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './api/baseApiConfig';
import config from '../config';

// Types
export interface ChatMessage {
	role: 'user' | 'assistant' | 'system';
	content: string;
	timestamp?: string;
	type?: string;
	name?: string;
	file_data?: any;
	webUrl?: string;
	id?: string;
}

export interface GrantedAgentChatRequest {
	userId: string;
	conversationId?: string;
	messages: ChatMessage[];
	promptPath?: string;
	model?: string;
	provider?: string;
}

export interface ChatHistoryResponse {
	messages: ChatMessage[];
	conversationId?: string;
	chatContent?: any[];
}

export interface ConversationSummary {
	conversationId: string;
	userId: string;
	title?: string;
	createdAt: string;
	updatedAt: string;
	messageCount: number;
	fileSharePath?: string;
	partitionKey?: string;
	rowKey?: string;
	timestamp?: string;
	eTag?: string;
}

export interface ChatHistoryArrayResponse {
	history: ConversationSummary[];
}

// Tags for cache invalidation
export const TAGS = {
	CHAT: 'Chat',
} as const;

// Create the API
export const aiApi = createApi({
	reducerPath: 'aiApi',
	baseQuery: createBaseQuery(`${config.APIM_BASE_URL}/ai`),
	tagTypes: [TAGS.CHAT],
	endpoints: builder => ({
		grantedAgentChat: builder.mutation<any, GrantedAgentChatRequest>({
			query: ({ userId, conversationId, messages, model, provider }) => {
				if (!userId) throw Error('Missing userId parameter');

				const params = new URLSearchParams();
				if (conversationId) {
					params.append('conversationId', conversationId);
				}
				if (model) {
					params.append('model', model);
				}
				if (provider) {
					params.append('provider', provider);
				}

				return {
					method: 'POST',
					url: `/grants-chat`,
					body: {
						conversationId: conversationId,
						chatContent: messages,
						systemPromptExtension: `Output Format Requirements:
								Strict HTML (<div> based): All output must be enclosed within a single <div> tag.
								No extraneous HTML: Do not include <html>, <body>, <head>, <footer>, or any other page-level tags.
								Markdown to HTML Conversion: Internally, convert your structured financial expertise into valid HTML elements (e.g., <h2> for headings, <p> for paragraphs, <ul> or <ol> for lists, <table> for tabular data, <strong> for emphasis).
								Template Adherence: If a specific content template is provided in the conversation, strictly follow its structure and formatting. Template is marked by prefix 'template:'. Template might contain information and content text. Don't remove or replace by default. Use existing content and implement generated content in the current document.
								Clean Output: Ensure there are no additional markings or non-HTML text outside the main <div>. Expected output is a valid HTML document.`,
					},
					params: params,
				};
			},
			transformResponse: (response: any) => {
				if (response?.response) {
					let cleanedContent = response?.response
						// Remove markdown code block markers
						.replace(/```html\n?/g, '')
						.replace(/```\n?/g, '')
						// Remove any remaining newlines
						.replace(/\n\r\n/g, '')
						.replace(/\n/g, '')
						// Remove any potential markdown backticks
						.replace(/`/g, '')
						// Trim whitespace
						.trim();

					// Ensure the content is wrapped in a div if it isn't already
					if (!cleanedContent.startsWith('<div>')) {
						cleanedContent = `<div>${cleanedContent}</div>`;
					}

					return {
						content: cleanedContent,
						conversationId: response.conversationId,
						currentDate: response.currentDate,
						conversationHistory: response.conversationHistory,
						metadata: response.metadata,
					};
				}
				throw new Error('No response content received');
			},
			invalidatesTags: (data, error, params) => {
				const invalidations = [];

				// If we have a conversation ID, invalidate its specific history
				if (params.conversationId) {
					invalidations.push({ type: TAGS.CHAT, id: `history-${params.userId}-${params.conversationId}` });
				}

				// Always invalidate the history array to refresh the conversation list
				invalidations.push({ type: TAGS.CHAT, id: `history-array-${params.userId}` });

				// If the response contains a new conversation ID, invalidate that too
				if (data?.conversationId && data.conversationId !== params.conversationId) {
					invalidations.push({ type: TAGS.CHAT, id: `history-${params.userId}-${data.conversationId}` });
				}

				return invalidations;
			},
			async onQueryStarted(params, { dispatch, queryFulfilled, getState }) {
				try {
					// Get the current user ID from the state or use a default
					const state = getState() as any;
					const currentUserId = state.auth?.user?.email || params.userId;

					// Check if we have existing history for this conversation
					if (params.conversationId) {
						const existingHistory = aiApi.endpoints.getGrantedAgentChatHistory.select({
							userId: currentUserId,
							conversationId: params.conversationId,
						})(state);

						// If no existing history, fetch it first
						if (!existingHistory.data) {
							await dispatch(
								aiApi.endpoints.getGrantedAgentChatHistory.initiate({
									userId: currentUserId,
									conversationId: params.conversationId,
								}),
							);
						}

						// Optimistic update - add the user message
						const optimisticPatchResult = dispatch(
							aiApi.util.updateQueryData(
								'getGrantedAgentChatHistory',
								{
									userId: currentUserId,
									conversationId: params.conversationId,
								},
								draft => {
									if (!draft.messages) {
										draft.messages = [];
									}
									draft.messages.push(params.messages[params.messages.length - 1]);
								},
							),
						);
						queryFulfilled.catch(optimisticPatchResult.undo);
					}

					// Wait for the response and update with assistant's reply
					let chatContent: any;
					const { data } = await queryFulfilled;

					if (typeof data === 'string') {
						chatContent = { role: 'assistant', content: 'An error occured while using AI.' };
					} else {
						chatContent = data;
					}

					// Determine which conversation ID to use for the update
					const targetConversationId = data?.conversationId || params.conversationId;

					// If we have a new conversation ID, we need to handle it differently
					if (data?.conversationId && data.conversationId !== params.conversationId) {
						// New conversation was created - we can't use optimistic update for this
						// The response will be handled by the component through onConversationIdChange
						console.info('New conversation created:', data.conversationId);
					} else if (targetConversationId) {
						// Same conversation - update the existing history
						dispatch(
							aiApi.util.updateQueryData(
								'getGrantedAgentChatHistory',
								{
									userId: currentUserId,
									conversationId: targetConversationId,
								},
								draft => {
									if (!draft.messages) {
										draft.messages = [];
									}

									if (Array.isArray(chatContent)) {
										draft.messages.push(...chatContent);
									} else {
										draft.messages.push({
											role: 'assistant',
											content: chatContent?.response ?? 'Ei vastausta',
										});
									}
								},
							),
						);
					}
				} catch (ex) {
					console.error(ex);
				}
			},
		}),
		getGrantedAgentChatHistoryArray: builder.query<ChatHistoryArrayResponse, { userId: string }>({
			query: ({ userId }) => {
				if (!userId) throw Error('Missing userId parameter');

				return {
					method: 'GET',
					url: `/grants-chat/history-array/${userId}`,
				};
			},
			providesTags: (_result, _err, data) => [{ type: TAGS.CHAT, id: `history-array-${data.userId}` }],
		}),
		getGrantedAgentChatHistory: builder.query<ChatHistoryResponse, { userId: string; conversationId: string | undefined }>({
			query: ({ userId, conversationId }) => {
				if (!userId) throw Error('Missing userId parameter');
				if (!conversationId) throw Error('Missing conversationId parameter');

				const params = new URLSearchParams();
				params.append('conversationId', conversationId);

				return {
					method: 'GET',
					url: `/grants-chat/history/${userId}`,
					params: params,
				};
			},
			providesTags: (_result, _err, data) => [{ type: TAGS.CHAT, id: `history-${data.userId}-${data.conversationId}` }],
		}),
		deleteGrantedAgentChatHistory: builder.mutation<any, { userId: string; conversationId: string }>({
			query: ({ userId, conversationId }) => {
				if (!userId) throw Error('Missing userId parameter');
				if (!conversationId) throw Error('Missing conversationId parameter');

				return {
					method: 'DELETE',
					url: `/grants-chat/history/${conversationId}`,
					headers: { 'X-Omit-Graph-Auth': 'true' },
				};
			},
			invalidatesTags: (data, error, params) => [
				// Invalidate specific conversation history
				{ type: TAGS.CHAT, id: `history-${params.userId}-${params.conversationId}` },
				// Invalidate all agent chat history arrays to refresh the conversation list
				{ type: TAGS.CHAT, id: `history-array-${params.userId}` },
				// Invalidate all chat tags to ensure complete cleanup
				TAGS.CHAT,
			],
			async onQueryStarted(params, { dispatch, queryFulfilled, getState }) {
				try {
					// Get the current user ID from the state or use a default
					const state = getState() as any;
					const currentUserId = state.auth?.user?.email || params.userId;

					// Immediately clear the conversation history from cache
					dispatch(
						aiApi.util.updateQueryData(
							'getGrantedAgentChatHistory',
							{
								userId: currentUserId,
								conversationId: params.conversationId,
							},
							draft => {
								// Clear all messages
								if (draft.messages) {
									draft.messages = [];
								}
								// Clear any other conversation data
								if (draft.chatContent) {
									draft.chatContent = [];
								}
							},
						),
					);

					// Wait for the delete operation to complete with detailed logging
					try {
						await queryFulfilled;
					} catch (e: any) {
						console.error('API delete error', {
							e,
							status: e?.status,
							originalStatus: e?.originalStatus,
							data: e?.data,
							error: e?.error,
						});
						throw e;
					}

					// After successful deletion, ensure the conversation list is refreshed
					dispatch(aiApi.util.invalidateTags([{ type: TAGS.CHAT, id: `history-array-${currentUserId}` }]));
				} catch (ex) {
					console.error('Error deleting agent chat history:', ex);
				}
			},
		}),
		createGrantedAgentConversation: builder.mutation<any, { userId: string; title?: string }>({
			query: ({ userId, title }) => {
				if (!userId) throw Error('Missing userId parameter');

				return {
					method: 'POST',
					url: `/conversations`,
					body: { userId, title: title || 'New Conversation' },
				};
			},
			invalidatesTags: (_result, _err, params) => [{ type: TAGS.CHAT, id: `history-array-${params.userId}` }],
		}),
		updateConversationTitle: builder.mutation<any, { userId: string; conversationId: string; title: string }>({
			query: ({ conversationId, title }) => {
				if (!conversationId) throw Error('Missing conversationId parameter');

				return {
					method: 'PUT',
					url: `/conversations/${conversationId}`,
					body: { title },
				};
			},
			invalidatesTags: (_result, _err, params) => [{ type: TAGS.CHAT, id: `history-array-${params.userId}` }],
		}),
		getProvidersAndModels: builder.query<any, void>({
			query: () => ({
				method: 'GET',
				url: `/static/providers-models`,
			}),
		}),
		getProviderModels: builder.query<any, { provider: string }>({
			query: ({ provider }) => ({
				method: 'GET',
				url: `/static/providers-models/${provider}`,
			}),
		}),
	}),
});

// Export hooks for use in components
export const {
	useGrantedAgentChatMutation,
	useGetGrantedAgentChatHistoryArrayQuery,
	useGetGrantedAgentChatHistoryQuery,
	useDeleteGrantedAgentChatHistoryMutation,
	useCreateGrantedAgentConversationMutation,
	useUpdateConversationTitleMutation,
	useGetProvidersAndModelsQuery,
	useGetProviderModelsQuery,
} = aiApi;

// Export the API for store configuration
export default aiApi;
